import { Form, Input, Modal, message } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import type { ChangePasswordFormValues } from "../types";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ChangePasswordModal({
  open,
  onClose,
}: ChangePasswordModalProps): JSX.Element {
  const { t } = useTranslation();
  const [form] = Form.useForm<ChangePasswordFormValues>();
  const [loading, setLoading] = useState<boolean>(false);

  const handleChangePassword = async (values: ChangePasswordFormValues): Promise<void> => {
    setLoading(true);

    try {
      await api.post("/auth/change-password", {
        old_password: values.old_password,
        new_password: values.new_password,
      });

      message.success(t("changePasswordModal.success"));
      form.resetFields();
      onClose();
    } catch (err) {
      console.error("Password change error:", err);
      const errorObj = err as { response?: { data?: { message?: string } } };
      if (errorObj.response?.data?.message) {
        message.error(errorObj.response.data.message);
      } else {
        message.error(t("changePasswordModal.failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (): void => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("changePasswordModal.title")}
      open={open}
      onCancel={handleCancel}
      onOk={form.submit}
      confirmLoading={loading}
      okText={t("changePasswordModal.okText")}
      cancelText={t("changePasswordModal.cancelText")}
    >
      <Form form={form} layout="vertical" onFinish={handleChangePassword}>
        <Form.Item
          label={t("changePasswordModal.currentPassword")}
          name="old_password"
          rules={[{ required: true, message: t("changePasswordModal.currentPasswordRequired") }]}
        >
          <Input.Password placeholder={t("changePasswordModal.currentPasswordPlaceholder")} />
        </Form.Item>

        <Form.Item
          label={t("changePasswordModal.newPassword")}
          name="new_password"
          rules={[
            { required: true, message: t("changePasswordModal.newPasswordRequired") },
            { min: 6, message: t("changePasswordModal.newPasswordMinLength") },
          ]}
        >
          <Input.Password placeholder={t("changePasswordModal.newPasswordPlaceholder")} />
        </Form.Item>

        <Form.Item
          label={t("changePasswordModal.confirmNewPassword")}
          name="confirm_password"
          rules={[
            { required: true, message: t("changePasswordModal.confirmNewPasswordRequired") },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("new_password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t("changePasswordModal.passwordsDoNotMatch")));
              },
            }),
          ]}
        >
          <Input.Password placeholder={t("changePasswordModal.confirmNewPasswordPlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
